import { Button, Input, Space, Switch, Tag, Tree, Typography } from "@arco-design/web-react";
import { useMemo, useState } from "react";

import { collectTreeKeys, filterRoleTree, resolvePermissionSelection } from "../service";
import type { AuthzTreeNode } from "../types";

type RolePermissionTreeProps = {
  tree: AuthzTreeNode[];
  selectedPermissionCodes: string[];
  onChange: (permissionCodes: string[]) => void;
};

/** 页面与操作权限严格勾选树。 */
export function RolePermissionTree({ tree, selectedPermissionCodes, onChange }: RolePermissionTreeProps) {
  const [keyword, setKeyword] = useState("");
  const [selectedOnly, setSelectedOnly] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>(() => collectTreeKeys(tree));
  const selectedSet = useMemo(() => new Set(selectedPermissionCodes), [selectedPermissionCodes]);
  const filteredTree = useMemo(
    () => filterRoleTree(tree, keyword, selectedOnly, selectedSet),
    [keyword, selectedOnly, selectedSet, tree],
  );
  const treeData = useMemo(() => mapPermissionTreeData(filteredTree), [filteredTree]);
  const checkedKeys = useMemo(
    () => tree.flatMap((node) => collectCheckedPermissionKeys(node, selectedSet)),
    [selectedSet, tree],
  );

  return (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Input.Search
          allowClear
          aria-label="搜索页面或操作权限"
          placeholder="搜索页面、操作或权限码"
          style={{ width: 340 }}
          value={keyword}
          onChange={setKeyword}
        />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <Switch checked={selectedOnly} onChange={setSelectedOnly} />
          <span>仅看已选</span>
        </label>
        <Button size="small" onClick={() => setExpandedKeys(collectTreeKeys(filteredTree))}>全部展开</Button>
        <Button size="small" onClick={() => setExpandedKeys([])}>全部收起</Button>
        <Typography.Text type="secondary">已选择 {selectedPermissionCodes.length} 项权限</Typography.Text>
      </div>
      <Tree
        blockNode
        checkable
        checkStrictly
        showLine
        treeData={treeData}
        checkedKeys={checkedKeys}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        onCheck={(_, extra) => {
          const node = extra.node as unknown as { key: string };
          const permissionCode = findPermissionCodeByKey(tree, String(node.key));
          if (!permissionCode) return;
          onChange(
            resolvePermissionSelection(
              tree,
              selectedPermissionCodes,
              permissionCode,
              extra.checked,
            ),
          );
        }}
      />
    </Space>
  );
}

/** 将权限节点映射为带风险标识的 Arco Tree 数据。 */
function mapPermissionTreeData(tree: AuthzTreeNode[]): Array<Record<string, unknown>> {
  return tree.map((node) => ({
    key: node.key,
    permissionCode: node.permissionCode,
    title: (
      <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
        <span>{node.title}</span>
        {node.type === "page" ? <Tag color="arcoblue">页面</Tag> : null}
        {node.type === "action" ? <Tag color="purple">操作</Tag> : null}
        {node.riskLevel === "high" ? <Tag color="red">高风险</Tag> : null}
        {node.riskLevel === "sensitive" ? <Tag color="orange">敏感</Tag> : null}
        {node.permissionCode ? <Typography.Text type="secondary">{node.permissionCode}</Typography.Text> : null}
      </span>
    ),
    disableCheckbox: node.type === "module",
    children: mapPermissionTreeData(node.children),
  }));
}

/** 收集当前已选权限对应的树节点 key。 */
function collectCheckedPermissionKeys(node: AuthzTreeNode, selected: Set<string>): string[] {
  return [
    ...(node.permissionCode && selected.has(node.permissionCode) ? [node.key] : []),
    ...node.children.flatMap((child) => collectCheckedPermissionKeys(child, selected)),
  ];
}

/** 按 Arco Tree 稳定节点 key 回查权限码。 */
function findPermissionCodeByKey(tree: AuthzTreeNode[], nodeKey: string): string | undefined {
  for (const node of tree) {
    if (node.key === nodeKey) return node.permissionCode;
    const childPermissionCode = findPermissionCodeByKey(node.children, nodeKey);
    if (childPermissionCode) return childPermissionCode;
  }
  return undefined;
}
